import { useTranslation } from "react-i18next";
import { ShowLoadingScreen } from "components/common/Loading";
import { GetSingleDataSet } from "network/wineDataSetAPI";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card } from "reactstrap";
import { mediaUrl } from "utils/mediaUrl";
import { formatDecimalWithComma } from "utils/formatDecimalWithComma";
import CountryList from "country-list-with-dial-code-and-flag";

// Stessa impostazione della scheda delle richieste (UnapprovedWineDataSingle).
const SECTIONS = [
  ["descriptive", [
    ["description", "wide"],
    ["appearance", "wide"],
    ["aroma", "wide"],
    ["taste", "wide"],
    ["pairing", "wide"],
  ]],
  ["origin", [
    ["country"],
    ["region"],
    ["colour"],
    ["denomination"],
    ["grapes"],
    ["gradation", "unit", "% vol"],
    ["format", "unit", "L"],
    ["terroir", "wide"],
    ["vinification", "wide"],
    ["fermentation", "wide"],
    ["ingredients", "wide"],
  ]],
  ["production", [
    ["bio", "bool"],
    ["natural", "bool"],
    ["biodynamic", "bool"],
    ["have_sulphite", "bool"],
    ["conventional", "bool"],
  ]],
  ["nutrition", [
    ["energy"],
    ["fat", "unit", "g"],
    ["saturated_fat", "unit", "g"],
    ["carbohydrate", "unit", "g"],
    ["sugar", "unit", "g"],
    ["protein", "unit", "g"],
    ["salt", "unit", "g"],
  ]],
];
const IMAGES = [
  ["front_bottle_image"],
  ["back_bottle_image"],
  ["front_label_image"],
  ["back_label_image"],
];

const isEmpty = (v) => v === null || v === undefined || v === "";

const imageSrc = mediaUrl;

const Field = ({ label, children, wide, filled }) => (
  <div className={`wd-field ${wide ? "wd-field-wide" : ""}`}>
    <div className="wd-field-label">{label}</div>
    <div className={`wd-field-value ${filled ? "" : "wd-field-empty"}`}>
      {filled ? children : "-"}
    </div>
  </div>
);

export default function SingleWineDetail() {
  const { t } = useTranslation("wine");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await GetSingleDataSet(id);
        setData(res?.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return <ShowLoadingScreen />;
  }
  if (!data) {
    return <div className="content my-4">{t("detail.notFound")}</div>;
  }

  const display = (key, type, unit) => {
    const v = data[key];
    if (type === "bool") return v === true ? t("common.yes") : t("common.no");
    if (type === "unit") return `${formatDecimalWithComma(v)}${unit ? ` ${unit}` : ""}`;
    if (key === "country") return CountryList.findOneByCountryCode(v)?.name || String(v);
    return String(v);
  };
  const filled = (key, type) =>
    type === "bool" ? typeof data[key] === "boolean" : !isEmpty(data[key]);

  return (
    <div className="content singlewinedetail">
      <div className="mt-4 singleWinepage wd-request">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4" style={{ gap: 12 }}>
          <Button onClick={() => navigate(-1)} className="btn btn-primary d-flex align-items-center">
            <i className="tim-icons icon-minimal-left mr-2"></i>
            <span>{t("common.back")}</span>
          </Button>
        </div>

        <h1 className="mb-1">{data.name}</h1>
        <p className="text-muted mb-4">
          {[data.winery, formatDecimalWithComma(data.vintage)].filter(Boolean).join(" · ")}
        </p>

        <Card className="wd-section">
          <h4 className="wd-section-title">{t("sections.main")}</h4>
          <div className="wd-grid">
            <Field label={t("fields.name")} filled={filled("name")}>{data.name}</Field>
            <Field label={t("fields.winery")} filled={filled("winery")}>{data.winery}</Field>
            <Field label={t("fields.vintage")} filled={filled("vintage")}>{formatDecimalWithComma(data.vintage)}</Field>
            <Field label={t("fields.ean")} filled={filled("ean")}>{data.ean}</Field>
          </div>
        </Card>

        <Card className="wd-section">
          <h4 className="wd-section-title">{t("sections.images")}</h4>
          <div className="row">
            {IMAGES.map(([key]) => (
              <div className="col-6 col-lg-3 mb-3" key={key}>
                <div className="wd-field-label">{t(`fields.${key}`)}</div>
                {data[key] ? (
                  <img
                    className="productImage"
                    style={{ objectFit: "cover" }}
                    src={imageSrc(data[key])}
                    alt={t(`fields.${key}`)}
                  />
                ) : (
                  <div className="wd-field-value wd-field-empty">{t("detail.noImage")}</div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {SECTIONS.map(([title, fields]) => (
          <Card className="wd-section" key={title}>
            <h4 className="wd-section-title">{t(`sections.${title}`)}</h4>
            <div className="wd-grid">
              {fields.map(([key, type, unit]) => (
                <Field key={key} label={t(`fields.${key}`)} wide={type === "wide"} filled={filled(key, type)}>
                  {display(key, type, unit)}
                </Field>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
